// Shared utility functions for theme components

/**
 * Check if the current viewport is below a configured breakpoint
 * Falls back to sensible defaults if config is missing
 * @param {string} name - Breakpoint name from umbrellaConfig.breakpoints (e.g. 'md', 'mdlg')
 * @returns {boolean}
 */
export function isBelowBreakpoint(name = 'md') {
  const config = window.umbrellaConfig || {};
  const breakpoints = config.breakpoints || {};
  const fallback = 768;
  const bp = typeof breakpoints[name] === 'number' ? breakpoints[name] : fallback;
  return window.innerWidth < bp;
}

/**
 * Check if the current viewport is mobile based on theme breakpoints
 * Uses the 'mdlg' breakpoint by default
 * @returns {boolean} True if viewport width is below the desired breakpoint
 */

export function isMobile() {
  return isBelowBreakpoint('mdlg');
}

/**
 * Format money amount to currency string
 * @param {number} cents - Amount in cents
 * @returns {string} Formatted currency string
 */
export function formatMoney(cents) {
  const amount = (cents / 100).toFixed(2);
  const symbol = window.umbrellaConfig.currency.symbol || '$';
  return `${symbol}${amount}`;
};

// Component Support Utilities

export const setSelectors = (context = document, selectors = {}) => {
  const elements = {};
  Object.keys(selectors).forEach((key) => {
    const el = context.querySelector(selectors[key]);
    if(el) elements[key] = el;
  });
  return elements;
};

export function addListeners(target, eventMap) {
  Object.entries(eventMap).forEach(([event, handler]) => {
    target.addEventListener(event, handler);
  });
};

// Normalize a variety of target inputs into an array of EventTargets
function normalizeTargets(targets) {
  if(!targets) return [];
  if(typeof targets.addEventListener === 'function') return [targets];
  if(Array.isArray(targets) || typeof targets.forEach === 'function') return Array.from(targets);
  if(typeof targets.length === 'number') return Array.from(targets);
  if(typeof targets === 'object') return Object.values(targets);
  return [];
};

// Enhanced helper to support a single target, an array/NodeList, or an object map of targets
export function assignListeners(targets, eventMap) {
  const targetList = normalizeTargets(targets);
  targetList.forEach((target) => {
    Object.entries(eventMap).forEach(([event, handler]) => {
      target.addEventListener(event, handler);
    });
  });
};

export function removeListeners(targets, eventMap) {
  const targetList = normalizeTargets(targets);
  targetList.forEach((target) => {
    Object.entries(eventMap).forEach(([event, handler]) => {
      target.removeEventListener(event, handler);
    });
  });
};